import { groupRows } from '@texas/utils/helpers/groupingHelpers';
import { FeatureGuard } from '@texas/components/shared/featureGuard/FeatureGuard';
import {
  Flex,
  TableContainer,
  Table,
  Thead,
  Text,
  Tr,
  Th,
  Tbody,
  Td,
  Checkbox,
  Box,
  Badge,
  Tooltip,
  Spinner,
} from '@chakra-ui/react';
import { CompositionGroup } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { PaImage } from '@texas/components/shared/productApproval/PaImage';
import { evaluateSampleNextAction } from '@texas/components/shared/samples/evaluateSampleNextAction';
import { VersionCurrentState } from '@texas/components/shared/samples/VersionCurrentState';
import { SampleNextAction } from '@texas/components/shared/samples/SampleNextAction';
import { MirroringTag } from '@texas/components/shared/suppliers/MirroringTag';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import React, { useMemo } from 'react';
import { TagWithTooltip } from '@texas/components/shared/TagWithTooltip';
import { useTranslation } from 'react-i18next';
import { CompositionGroupHistoryModal } from '../../history/CompositionGroupHistoryModal';
import { SampleLifecycle } from '@texas/components/shared/samples/types';
import { getRowId } from '@texas/components/shared/samples/helper';
import { useActiveContext } from '@texas/hooks/useActiveContext';
import { MultiSelectContext } from '../Versions';
import { VersionsMenu } from './VersionsMenu';
import { ensureEnumNumber } from '@texas/utils/helpers/enumHelpers';
import { ConfigureCompositionGroupGarp } from '@texas/messageBus/messages';
import { usePublisher } from '@texas/messageBus/useMessageBus';
import { useVersionOverviewContext } from './VersionOverviewContext';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { ProductApprovalState } from '@texas/api/endpoints/productApprovalsApi';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { Locale } from '@texas/i18n/types';
import { VersionMaterialsSummary } from './VersionMaterialsSummary';

export interface CopyProps {
  groupId: number;
  supplierId: number;
  branchId: number;
  basedOnId: number | null;
  cellX: number | null;
  cellY: number | null;
  replaceId: number | null;
}

export interface EmptyCell {
  cellX: number;
  cellY: number;
  xLabel: string;
  yLabel: string;
}

export function GroupedVersions({
  header,
  versions,
  onRestoreOpen,
  onArchiveOpen,
  onOpen,
  onCopyOpen,
  versionsLoading,
  showCellColumns,
  emptyCells,
}: {
  header: React.ReactNode;
  versions: CompositionGroup[];
  onRestoreOpen: (id: number) => void;
  onArchiveOpen: (id: number) => void;
  onOpen: (id: number, readOnly: boolean, isArchived: boolean) => void;
  onCopyOpen: (props: CopyProps) => void;
  versionsLoading: boolean;
  showCellColumns?: boolean;
  emptyCells?: EmptyCell[];
}) {
  const { t } = useTranslation();
  const { filters } = useVersionOverviewContext();
  const { publish } = usePublisher();

  const { selectedRows, setSelectedRows } =
    useActiveContext(MultiSelectContext);
  const {
    isOpen: isHistoryOpen,
    onClose: onHistoryClose,
    onOpen: onHistoryOpen,
    value: historyValue,
  } = useValueDisclosure<{
    id: number;
    name: string;
  }>();

  const filteredVersions = useMemo(() => {
    if (filters.showArchived) return versions;
    return versions.filter((v) => !v.archived);
  }, [filters.showArchived, versions]);

  const versionGroups = showCellColumns
    ? groupRows(filteredVersions, (v) =>
        v.cellX == null
          ? `version-${v.id}`
          : JSON.stringify([
              v.controlledById ?? v.id,
              v.supplierId,
              v.branchId,
              v.version,
              v.cellX,
            ]),
      )
    : [{ key: 'all', rows: filteredVersions }];
  const emptyGroups = showCellColumns
    ? groupRows(emptyCells ?? [], (cell) => String(cell.cellX))
    : [{ key: 'empty', rows: emptyCells ?? [] }];
  const groupStyles = showCellColumns
    ? {
        '& > tr:first-of-type > td, & > tr:first-of-type > th': {
          borderTopWidth: '1px',
          borderTopColor: 'entityDirectory.border',
        },
      }
    : undefined;

  return (
    <Flex flexDir="column" pt={6}>
      <Flex gap={4}>{header}</Flex>
      {versionsLoading && <Spinner mt={2} label={t('general.loading')} />}
      {!versionsLoading &&
        (filteredVersions.length === 0 && !emptyCells?.length ? (
          <Text variant="sub">{t('general.noData')}</Text>
        ) : (
          <TableContainer>
            <Table variant="version-overview">
              <Thead>
                <Tr>
                  {showCellColumns && <Th />}
                  {showCellColumns && <Th />}
                  <Th w="40px" />
                  <Th>{t('general.status')}</Th>
                  <Th w="72px">{t('general.photo')}</Th>
                  <Th>{t('suppliers.supplier')}</Th>
                  <Th>{t('general.branch')}</Th>
                  <Th>{t('general.option')}</Th>
                  <Th>{t('general.version')}</Th>
                  <FeatureGuard hideBadge={true}>
                    <Th>{t('inquiry.inquiry')}</Th>
                  </FeatureGuard>
                  <Th>{t('productApproval.number')}</Th>
                  <Th>{t('general.materials')}</Th>
                  <Th w="48px" />
                </Tr>
              </Thead>
              {versionGroups.map((group, groupIndex) => (
                <Tbody key={group.key} sx={groupStyles}>
                  {group.rows.map((v, rowIndex) => {
                    const lifecycle = v.mainProductApproval?.mainSample
                      ? v.mainProductApproval.mainSample.lifecycle
                      : SampleLifecycle.Nothing;

                    const nextAction = evaluateSampleNextAction({
                      state: lifecycle,
                      sampleId: v.mainProductApproval?.mainSample?.id,
                      versionArchived: !!v.archived,
                    });

                    return (
                      <Tr
                        key={v.id}
                        role="group"
                        data-selected={
                          selectedRows[v.id] === true ? 'true' : undefined
                        }
                        className={
                          !v.controlledById && v.matrixNodeXValue
                            ? 'unique'
                            : undefined
                        }
                      >
                        {showCellColumns && rowIndex === 0 && (
                          <Td
                            as="th"
                            scope="rowgroup"
                            rowSpan={group.rows.length}
                            bg="entityDirectory.raisedSurface"
                            verticalAlign="middle"
                            textAlign="left"
                            fontWeight="semibold"
                          >
                            <Text>{v.matrixNodeXValue}</Text>
                          </Td>
                        )}
                        {showCellColumns && (
                          <Td>
                            <Text>{v.matrixNodeYValue}</Text>
                          </Td>
                        )}
                        <Td>
                          <Checkbox
                            isChecked={selectedRows[v.id] === true}
                            onChange={(e) => {
                              setSelectedRows((r) => ({
                                ...r,
                                [getRowId(v.id)]: e.target.checked,
                              }));
                            }}
                          />
                        </Td>
                        <Td>
                          <VersionRowStatus version={v} lifecycle={lifecycle} />
                        </Td>
                        <Td>
                          {v.mainProductApproval && (
                            <PaImage
                              fileIdentifier={
                                v.mainProductApproval.fileIdentifier
                              }
                              fileId={v.mainProductApproval.fileId}
                              paId={v.mainProductApproval.productApprovalId}
                              paNumber={v.mainProductApproval.formatNumber}
                              itemCode={v.articleItemCode}
                              articleFolderId={v.folderId}
                              matrixNodeXValue={v.matrixNodeXValue}
                              matrixNodeYValue={v.matrixNodeYValue}
                              maxW="48px"
                              maxH="56px"
                            />
                          )}
                        </Td>
                        <Td>
                          <Text
                            maxW="200px"
                            whiteSpace="normal"
                            fontWeight="semibold"
                          >
                            {v.supplierName}
                          </Text>
                          {!!v.supplierTags?.length && (
                            <Flex gap={1} wrap="wrap" mt={1}>
                              {v.supplierTags.map((tag) => (
                                <TagWithTooltip key={tag.id} tag={tag} />
                              ))}
                            </Flex>
                          )}
                          {v.followers && (
                            <MirroringTag
                              followers={v.followers}
                              showCount={true}
                            />
                          )}
                        </Td>
                        <Td>
                          <Badge
                            variant="subtle"
                            colorScheme="gray"
                            fontSize="10px"
                            px={2}
                            py={1}
                          >
                            {v.branchIdentifier}
                          </Badge>
                        </Td>
                        <Td>
                          {v.basedOnGroupVersion ? (
                            <Box maxW="180px">
                              <Text fontWeight="semibold">
                                {t('compositionGroup.option.optionNr', {
                                  nr: v.basedOnGroupVersion,
                                })}
                              </Text>
                              {v.basedOnGroupName && (
                                <Text
                                  fontSize="xs"
                                  color="entityDirectory.mutedText"
                                  whiteSpace="normal"
                                >
                                  {v.basedOnGroupName}
                                </Text>
                              )}
                            </Box>
                          ) : (
                            <Text color="entityDirectory.mutedText">—</Text>
                          )}
                        </Td>
                        <Td>
                          <Tooltip
                            label={t('compositionGroup.version.versionNr', {
                              nr: v.version,
                            })}
                          >
                            <Text
                              className="version-number"
                              fontSize="xl"
                              fontWeight="bold"
                              letterSpacing="tight"
                            >
                              {t('compositionGroup.version.compactVersion', {
                                nr: v.version,
                              })}
                            </Text>
                          </Tooltip>
                        </Td>
                        <FeatureGuard hideBadge={true}>
                          <Td>
                            {v.inquiryId && v.formattedInquiryNumber ? (
                              <AngularNavLink
                                to="variant.inquiry.detail"
                                navobj={{ inquiryId: v.inquiryId }}
                              >
                                {v.formattedInquiryNumber}
                              </AngularNavLink>
                            ) : (
                              <Text color="gray.500">-</Text>
                            )}
                          </Td>
                        </FeatureGuard>
                        <Td>
                          <Text fontSize="xs">
                            {v.mainProductApproval?.formatNumber ?? '—'}
                          </Text>
                        </Td>
                        <Td>
                          <VersionMaterialsSummary
                            compositions={v.compositions}
                          />
                        </Td>
                        <Td>
                          <Flex align="center" gap={2}>
                            {!v.archived && (
                              <SampleNextAction
                                action={nextAction}
                                lifecycle={lifecycle}
                                transportRequestId={
                                  v.mainProductApproval?.mainSample?.transport
                                    ?.id
                                }
                                sampleId={v.mainProductApproval?.mainSample?.id}
                                groupId={v.id}
                                pa={
                                  v.mainProductApproval
                                    ? {
                                        id: v.mainProductApproval
                                          .productApprovalId,
                                        state: v.mainProductApproval.state,
                                      }
                                    : undefined
                                }
                                variantId={v.variantId}
                                supplierId={v.supplierId!}
                                articleFolderId={v.folderId}
                                branchId={v.branchId!}
                                shipmentId={
                                  v.mainProductApproval?.mainSample?.transport
                                    ?.shipment?.id
                                }
                              />
                            )}
                            <VersionsMenu
                              version={v}
                              onRestoreOpen={onRestoreOpen}
                              onArchiveOpen={onArchiveOpen}
                              onOpen={onOpen}
                              onCopyOpen={onCopyOpen}
                              onHistoryOpen={(id, name) =>
                                onHistoryOpen({ id, name })
                              }
                              basedOnGroupVersion={v.basedOnGroupVersion ?? 0}
                              onSetGarpDataOpen={
                                v.linkedVariantId && v.branchId
                                  ? () =>
                                      publish(
                                        new ConfigureCompositionGroupGarp(
                                          v.id,
                                          v.linkedVariantId!,
                                          v.branchId!,
                                          v.mainProductApproval?.productApprovalId,
                                        ),
                                      )
                                  : undefined
                              }
                            />
                          </Flex>
                        </Td>
                      </Tr>
                    );
                  })}
                  {showCellColumns &&
                    (groupIndex < versionGroups.length - 1 ||
                      !!emptyCells?.length) && (
                      <Tr aria-hidden="true">
                        <Td h={3} p={0} border={0} />
                      </Tr>
                    )}
                </Tbody>
              ))}
              {emptyGroups.map((group, groupIndex) => (
                <Tbody key={`empty-${group.key}`} sx={groupStyles}>
                  {group.rows.map(
                    ({ cellX, cellY, xLabel, yLabel }, rowIndex) => (
                      <Tr
                        key={`empty-${cellX}-${cellY}`}
                        opacity={0.4}
                        borderStyle="dashed"
                      >
                        {showCellColumns && rowIndex === 0 && (
                          <Td
                            as="th"
                            scope="rowgroup"
                            rowSpan={group.rows.length}
                            bg="entityDirectory.raisedSurface"
                            verticalAlign="middle"
                            textAlign="left"
                            fontWeight="semibold"
                          >
                            <Text>{xLabel}</Text>
                          </Td>
                        )}
                        {showCellColumns && (
                          <Td>
                            <Text>{yLabel}</Text>
                          </Td>
                        )}
                        <Td>
                          <Text color="entityDirectory.mutedText">
                            {t('general.empty')}
                          </Text>
                        </Td>
                        <Td colSpan={6} />
                        <FeatureGuard hideBadge={true}>
                          <Td />
                        </FeatureGuard>
                        <Td colSpan={3} />
                      </Tr>
                    ),
                  )}
                  {showCellColumns && groupIndex < emptyGroups.length - 1 && (
                    <Tr aria-hidden="true">
                      <Td h={3} p={0} border={0} />
                    </Tr>
                  )}
                </Tbody>
              ))}
            </Table>
          </TableContainer>
        ))}

      {historyValue && (
        <CompositionGroupHistoryModal
          isOpen={isHistoryOpen}
          onClose={onHistoryClose}
          compositionGroupId={historyValue.id}
          name={historyValue.name}
        />
      )}
    </Flex>
  );
}

function VersionRowStatus({
  version,
  lifecycle,
}: {
  version: CompositionGroup;
  lifecycle: SampleLifecycle;
}) {
  const { t } = useTranslation();
  const pa = version.mainProductApproval;
  const state = pa
    ? ensureEnumNumber(ProductApprovalState, pa.state)
    : undefined;
  const sampleState = ensureEnumNumber(SampleLifecycle, lifecycle);
  const status = version.archived
    ? { label: t('general.archived'), color: 'gray' }
    : sampleState === SampleLifecycle.SampleLost
    ? { label: t('sample.isLost'), color: 'red' }
    : state === ProductApprovalState.Accepted
    ? { label: t('sample.accepted'), color: 'green' }
    : state === ProductApprovalState.Rejected
    ? { label: t('sample.rejected'), color: 'red' }
    : state === ProductApprovalState.Cancelled
    ? { label: t('productApproval.cancelled'), color: 'gray' }
    : state === ProductApprovalState.OnHold
    ? { label: t('sample.onHold'), color: 'orange' }
    : pa?.mainSample
    ? { label: t('compositionGroup.version.inSampling'), color: 'orange' }
    : pa
    ? { label: t('productApproval.pending'), color: 'blue' }
    : { label: t('compositionGroup.version.notSampled'), color: 'gray' };
  const date =
    version.archived ?? pa?.handledDate ?? pa?.mainSample?.sampleReadyDate;

  return (
    <Flex direction="column" align="start" gap={1.5}>
      <Flex align="center" gap={2}>
        <VersionCurrentState
          archived={version.archived}
          lifecycle={lifecycle}
          paState={pa?.state}
          sampleReadyDate={pa?.mainSample?.sampleReadyDate ?? null}
        />
        <Badge
          colorScheme={status.color}
          fontSize="10px"
          fontWeight="semibold"
          whiteSpace="nowrap"
        >
          {status.label}
        </Badge>
      </Flex>
      {date && (
        <Text fontSize="11px" color="entityDirectory.mutedText">
          {formatDate(Locale.En, date)}
        </Text>
      )}
    </Flex>
  );
}
